import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalVariant, Skeleton } from '@patternfly/react-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { Question, TQuestion } from '../../api/questions/Questions';
import { useIsGranted } from '../user/ApplicationProvider';
import { Permission } from '../../enums/permission.enum';
import DiscoverAddQuestionModal from '../../helpers/helper-components/DiscoverAddQuestionModal';
import DeleteConfirmationModal from '../../helpers/helper-components/DeleteConfirmationModal';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import { AlertVariant } from '@patternfly/react-core';

interface DiscoverQuestionsModalProps {
	isOpen: boolean;
	onClose: () => void;
	chartId?: number;
	dashboardId?: number;
}

const DiscoverQuestionsModal: React.FC<DiscoverQuestionsModalProps> = ({
	isOpen,
	onClose,
	chartId,
	dashboardId,
}) => {
	const [questions, setQuestions] = useState<TQuestion[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isAddEditOpen, setIsAddEditOpen] = useState(false);
	const [editQuestion, setEditQuestion] = useState<TQuestion | undefined>();
	const [deleteQuestionId, setDeleteQuestionId] = useState<number | null>(null);
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);
	const isGranted = useIsGranted();
	const hasCreatePerm = isGranted(Permission.CreateQuestion);
	const hasEditPerm = isGranted(Permission.EditQuestion);
	const hasDeletePerm = isGranted(Permission.DeleteQuestion);
	const { addToast } = useToast();

	const fetchQuestions = () => {
		setIsLoading(true);
		Question.GetAll(chartId, dashboardId)
			.then((data) => {
				setQuestions(data);
			})
			.catch(() => {
				setQuestions([]);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	useEffect(() => {
		if (isOpen) {
			fetchQuestions();
		}
	}, [isOpen]);

	const handleEdit = (question: TQuestion) => {
		Question.Get(question.id!, ['tags'])
			.then((full) => {
				setEditQuestion(full);
				setIsAddEditOpen(true);
			})
			.catch(() => {
				addToast('Error loading question', AlertVariant.danger);
			});
	};

	const handleDeleteConfirm = () => {
		if (deleteQuestionId) {
			Question.Delete(deleteQuestionId)
				.then(() => {
					addToast('Deleted Discover Question Successfully', AlertVariant.success);
					setIsDeleteOpen(false);
					setDeleteQuestionId(null);
					fetchQuestions();
				})
				.catch(() => {
					addToast('Error deleting Discover Question', AlertVariant.danger);
				});
		}
	};

	const handleAddEditClose = (hasAdded = false) => {
		setIsAddEditOpen(false);
		setEditQuestion(undefined);
		if (hasAdded) {
			fetchQuestions();
		}
	};

	return (
		<>
			<Modal
				title="Discover Questions"
				variant={ModalVariant.medium}
				isOpen={isOpen}
				onClose={onClose}
				className="discover-questions-modal"
			>
				{hasCreatePerm && (
					<div style={{ marginBottom: '1rem' }}>
						<Button
							variant="link"
							onClick={() => {
								setEditQuestion(undefined);
								setIsAddEditOpen(true);
							}}
						>
							<FontAwesomeIcon icon={faPlus} /> Add Question
						</Button>
					</div>
				)}

				{isLoading ? (
					<div>
						<Skeleton />
						<span className="spacer-xxs" />
						<Skeleton width="80%" />
						<span className="spacer-xxs" />
						<Skeleton width="60%" />
					</div>
				) : questions.length === 0 ? (
					<div style={{ textAlign: 'center', color: '#6a6e73', padding: '2rem 0' }}>
						No questions found.
					</div>
				) : (
					<div className="discover-questions-list">
						{questions.map((q) => (
							<div
								key={q.id}
								className="discover-questions-list__item"
							>
								<div className="discover-questions-list__text">
									<div className="discover-questions-list__name">{q.name}</div>
									{q.description && (
										<div className="discover-questions-list__desc">
											{q.description}
										</div>
									)}
								</div>
								<div className="discover-questions-list__actions">
									{hasEditPerm && (
										<Button
											variant="plain"
											onClick={() => handleEdit(q)}
											aria-label={`Edit question: ${q.name}`}
										>
											<FontAwesomeIcon icon={faPencil} />
										</Button>
									)}
									{hasDeletePerm && (
										<Button
											variant="plain"
											onClick={() => {
												setDeleteQuestionId(q.id!);
												setIsDeleteOpen(true);
											}}
											aria-label={`Delete question: ${q.name}`}
										>
											<FontAwesomeIcon icon={faTrash} />
										</Button>
									)}
								</div>
							</div>
						))}
					</div>
				)}
			</Modal>

			<DiscoverAddQuestionModal
				isOpen={isAddEditOpen}
				onClose={handleAddEditClose}
				chartId={chartId ?? 0}
				fetchedQuestion={editQuestion}
				refetchQuestions={fetchQuestions}
				isDashboard={!!dashboardId}
			/>

			<DeleteConfirmationModal
				isOpen={isDeleteOpen}
				onClose={() => {
					setIsDeleteOpen(false);
					setDeleteQuestionId(null);
				}}
				onSubmit={handleDeleteConfirm}
			/>
		</>
	);
};

export default DiscoverQuestionsModal;
